import { Plane, Raycaster, Vector2, Vector3, type Camera } from 'three';

/**
 * Turns a pointer event into a point on the gameplay plane.
 *
 * The temptation is to skip this: waves live on z = 0, the camera looks straight
 * at them, so a bit of arithmetic on `clientX` and the canvas rect would seem to
 * do. It would — until the camera breathes, or the field of view changes, or the
 * canvas is letterboxed, or a phase adds a camera move on a hit. At that point
 * the visible seal and the tappable seal would quietly stop being the same
 * thing, and the bug would look like "the judging is unreliable".
 *
 * Casting a ray through the real camera makes the two agree by construction: the
 * point returned is the point under the cursor in the same projection that drew
 * the frame, whatever the camera is doing.
 */
export class WorldPointer {
  /** The plane the waves live on. Waves are flat, at z = 0. */
  private readonly plane = new Plane(new Vector3(0, 0, 1), 0);
  private readonly raycaster = new Raycaster();
  private readonly ndc = new Vector2();
  private readonly hit = new Vector3();

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly camera: Camera,
  ) {}

  /**
   * World point under a pointer event, or null if the ray misses the plane.
   *
   * Uses the canvas's own bounding rect rather than the window, so CSS scaling,
   * device pixel ratio and any surrounding layout are all already accounted for.
   */
  toWorld(clientX: number, clientY: number, out = { x: 0, y: 0 }): { x: number; y: number } | null {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;

    this.ndc.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -(((clientY - rect.top) / rect.height) * 2 - 1),
    );
    this.raycaster.setFromCamera(this.ndc, this.camera);
    // Parallel ray, or a plane behind the camera. Cannot happen with the current
    // framing, but returning a stale point would be worse than returning none.
    if (this.raycaster.ray.intersectPlane(this.plane, this.hit) === null) return null;

    out.x = this.hit.x;
    out.y = this.hit.y;
    return out;
  }
}
